import { motion } from 'framer-motion'
import { FileText, Image, Video, File, Clock, Eye, Edit, Trash2, MoreVertical } from 'lucide-react'
import { useState, useEffect } from 'react'
import { hoverLift, scaleIn, getWillChangeStyle } from '../lib/animations/optimized-animations'

export interface ContentItem {
  id: string
  title: string
  description: string | null
  type: 'presentation' | 'pdf' | 'image' | 'video' | 'markdown'
  filePath: string
  thumbnailUrl: string | null
  accessLevel: 'demo' | 'company' | 'admin'
  companyId: string | null
  metadata: { tags?: string[]; [key: string]: any } | null
  createdAt: string
  updatedAt: string
}

interface ContentCardProps {
  content: ContentItem
  onClick?: (content: ContentItem) => void
  onEdit?: (content: ContentItem) => void
  onDelete?: (content: ContentItem) => void
}

export function ContentCard({ content, onClick, onEdit, onDelete }: ContentCardProps) {
  const [imageError, setImageError] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [userRole, setUserRole] = useState<string | null>(null)

  useEffect(() => {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      setUserRole(user.role)
    }
  }, [])

  const canEdit = userRole === 'company_admin' || userRole === 'super_admin'

  const getFileIcon = () => {
    if (content.type === 'image') return <Image className="w-6 h-6" />
    if (content.type === 'video') return <Video className="w-6 h-6" />
    if (content.type === 'pdf') return <FileText className="w-6 h-6" />
    return <File className="w-6 h-6" />
  }

  const getTypeColor = () => {
    switch (content.type) {
      case 'presentation':
        return 'bg-primary/10 text-primary border-primary/20'
      case 'pdf':
        return 'bg-destructive/10 text-destructive border-destructive/20'
      case 'image':
        return 'bg-accent/10 text-accent border-accent/20'
      case 'video':
        return 'bg-secondary/10 text-secondary border-secondary/20'
      case 'markdown':
        return 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20'
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20'
    }
  }

  const getAccessLevelColor = () => {
    switch (content.accessLevel) {
      case 'demo':
        return 'bg-primary/10 text-primary'
      case 'company':
        return 'bg-secondary/10 text-secondary'
      case 'admin':
        return 'bg-destructive/10 text-destructive'
      default:
        return 'bg-muted/10 text-muted-foreground'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <motion.div
      className="group relative bg-card rounded-lg border border-border hover:shadow-lg transition-all cursor-pointer overflow-hidden"
      {...hoverLift}
      onClick={() => onClick?.(content)}
      {...scaleIn}
      style={getWillChangeStyle(['opacity', 'transform'])}
    >
      {/* Thumbnail / Preview */}
      <div className="aspect-video bg-secondary relative overflow-hidden">
        {content.thumbnailUrl && !imageError ? (
          <img
            src={`http://localhost:3000${content.thumbnailUrl}`}
            alt={content.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-110"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            {getFileIcon()}
          </div>
        )}

        {/* Type Badge */}
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-1 text-xs font-medium rounded border ${getTypeColor()}`}>
            {content.type}
          </span>
        </div>

        {/* Edit/Delete Menu (for admins) */}
        {canEdit && (
          <div className="absolute top-2 right-2">
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation()
                  setShowMenu(!showMenu)
                }}
                className="p-1.5 bg-background/80 hover:bg-background rounded-lg text-foreground backdrop-blur-sm transition-colors"
                aria-label="More options"
                style={getWillChangeStyle(['transform'])}
              >
                <MoreVertical className="w-4 h-4" />
              </motion.button>

              {showMenu && (
                <div className="absolute right-0 top-full mt-1 bg-popover rounded-lg shadow-lg border border-border py-1 min-w-30 z-dropdown-content">
                  <motion.button
                    whileHover={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowMenu(false)
                      onEdit?.(content)
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                    style={getWillChangeStyle(['transform', 'background-color'])}
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ backgroundColor: 'rgba(239,68,68,0.1)' }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowMenu(false)
                      onDelete?.(content)
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Eye className="w-8 h-8 text-white" />
        </div>
      </div>

      {/* Content Info */}
      <div className="p-4">
        {/* Title */}
        <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {content.title}
        </h3>

        {/* Description */}
        {content.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {content.description}
          </p>
        )}

        {/* Tags */}
        {content.metadata?.tags && content.metadata.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {content.metadata.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-0.5 text-xs bg-secondary text-secondary-foreground rounded"
              >
                {tag}
              </span>
            ))}
            {content.metadata.tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs bg-secondary text-secondary-foreground rounded">
                +{content.metadata.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{formatDate(content.createdAt)}</span>
          </div>
          <span className={`px-2 py-1 rounded font-medium ${getAccessLevelColor()}`}>
            {content.accessLevel}
          </span>
        </div>
      </div>
    </motion.div>
  )
}
